/**
 * Excel document helpers — read selection / used range, insert results.
 */

export interface ExcelContext {
  /** A1-style address of the active selection, e.g. "Sheet1!B2:D10". */
  selectionAddress: string;
  /** Selection values as TSV (tab-separated) — best for LLM context. */
  selectionTsv: string;
  /** Active sheet name. */
  sheetName: string;
  /** Used-range address on the active sheet. */
  usedRangeAddress: string;
  /** Used-range values truncated to MAX_ROWS x MAX_COLS for context. */
  usedRangeTsv: string;
  truncated: boolean;
  rowCount: number;
  colCount: number;
}

const MAX_ROWS = 200;
const MAX_COLS = 40;

export async function readExcelContext(): Promise<ExcelContext> {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getActiveWorksheet();
    const sel = ctx.workbook.getSelectedRange();
    const used = sheet.getUsedRangeOrNullObject(true);

    sheet.load('name');
    sel.load(['address', 'values', 'rowCount', 'columnCount']);
    used.load(['address', 'values', 'rowCount', 'columnCount', 'isNullObject']);
    await ctx.sync();

    const selectionTsv = valuesToTsv(sel.values || []);
    const truncated =
      !used.isNullObject && (used.rowCount > MAX_ROWS || used.columnCount > MAX_COLS);

    let usedRangeTsv = '';
    let usedRangeAddress = '';
    let rowCount = 0;
    let colCount = 0;
    if (!used.isNullObject) {
      usedRangeAddress = used.address;
      rowCount = used.rowCount;
      colCount = used.columnCount;
      if (truncated) {
        // Re-fetch a clamped sub-range so we never ship a 1M-cell sheet to the LLM.
        const clamped = sheet
          .getRange(used.address)
          .getResizedRange(Math.min(MAX_ROWS, rowCount) - rowCount, Math.min(MAX_COLS, colCount) - colCount);
        clamped.load('values');
        await ctx.sync();
        usedRangeTsv = valuesToTsv(clamped.values || []);
      } else {
        usedRangeTsv = valuesToTsv(used.values || []);
      }
    }

    return {
      selectionAddress: sel.address,
      selectionTsv,
      sheetName: sheet.name,
      usedRangeAddress,
      usedRangeTsv,
      truncated,
      rowCount,
      colCount,
    };
  });
}

function valuesToTsv(values: unknown[][]): string {
  return values
    .map((row) =>
      row
        .map((c) => {
          const s = c === null || c === undefined ? '' : String(c);
          // Escape tabs/newlines so each cell stays on its own column.
          return s.replace(/\t/g, '    ').replace(/\r?\n/g, ' ');
        })
        .join('\t'),
    )
    .join('\n');
}

/** Write a value, formula, or 2D array into the active selection (top-left anchored). */
export async function writeToSelection(payload: string): Promise<void> {
  return Excel.run(async (ctx) => {
    const sel = ctx.workbook.getSelectedRange();
    sel.load(['address']);
    await ctx.sync();

    if (payload.startsWith('=')) {
      // Single formula — apply to the whole selection (Excel will fill).
      sel.formulas = [[payload]];
    } else if (payload.includes('\t') || payload.includes('\n')) {
      const matrix = parseTsv(payload);
      const sheet = ctx.workbook.worksheets.getActiveWorksheet();
      const anchor = sheet.getRange(sel.address).getCell(0, 0);
      const target = anchor.getResizedRange(matrix.length - 1, (matrix[0]?.length || 1) - 1);
      target.values = matrix;
    } else {
      sel.values = [[payload]];
    }
    await ctx.sync();
  });
}

function parseTsv(text: string): (string | number)[][] {
  return text.split(/\r?\n/).filter(Boolean).map((row) =>
    row.split('\t').map((c) => {
      const n = Number(c);
      return c !== '' && !Number.isNaN(n) ? n : c;
    }),
  );
}
